import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, MapPin, CheckCircle2, AlertTriangle, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const CONF_STYLE = {
  high:   { chip: "bg-[#E9F0EA] text-[#3B5C46] border-[#B7CDBB]", label: "High" },
  medium: { chip: "bg-[#FBF3E1] text-[#6B4E1E] border-[#E1D0AA]", label: "Medium" },
  low:    { chip: "bg-[#F7E9E5] text-[#8A3E30] border-[#E7C9C1]", label: "Low" },
};

export default function EnrichAddressesPage() {
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [picks, setPicks] = useState(new Set());
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const scan = async () => {
    if (!window.confirm("Look up addresses for all facilities missing one? This uses AI and takes ~2 minutes.")) return;
    setScanning(true);
    setProposals([]);
    setPicks(new Set());
    setProgress({ done: 0, total: 0 });
    try {
      const PAGE = 12;
      let offset = 0;
      const all = [];
      // First page
      while (true) {
        const { data } = await api.post(`/facilities/enrich-addresses/scan?limit=${PAGE}&offset=${offset}`);
        const rows = data?.proposals || [];
        all.push(...rows);
        setProposals([...all]);
        setProgress({ done: all.length, total: data?.total || all.length });
        if (!data?.has_more) break;
        offset += data.returned || PAGE;
      }
      // Pre-select all high + medium
      const auto = new Set(all.filter((r) => r.confidence !== "low" && r.address).map((r) => r.facility_id));
      setPicks(auto);
      toast.success(`Found ${all.length} suggestions — ${auto.size} pre-selected as safe`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const toggle = (fid) => {
    setPicks((prev) => {
      const next = new Set(prev);
      if (next.has(fid)) next.delete(fid); else next.add(fid);
      return next;
    });
  };

  const apply = async () => {
    if (picks.size === 0) return;
    if (!window.confirm(`Save address for ${picks.size} facilities?`)) return;
    setApplying(true);
    try {
      const updates = proposals.filter((p) => picks.has(p.facility_id));
      const { data } = await api.post("/facilities/enrich-addresses/apply", { updates });
      toast.success(`Applied to ${data?.applied ?? 0} facilities`);
      // Remove applied from list
      setProposals((prev) => prev.filter((p) => !picks.has(p.facility_id)));
      setPicks(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const byConf = { high: 0, medium: 0, low: 0 };
  proposals.forEach((p) => byConf[p.confidence] !== undefined && byConf[p.confidence]++);

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <Link to="/facilities" className="inline-flex items-center gap-1 text-sm text-[#5C5850] hover:text-[#4A6B5D]" data-testid="back-to-facilities">
        <ArrowLeft className="h-4 w-4" /> All facilities
      </Link>

      <div className="mt-4 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A]">One-time cleanup</div>
          <h1 className="heading text-3xl sm:text-4xl font-bold text-[#292723] mt-2">Address enrichment</h1>
          <p className="text-sm text-[#5C5850] mt-1.5 max-w-2xl">
            AI looks up each facility's address across <span className="font-medium">NY / NJ / PA / IL</span>, then verifies with OpenStreetMap.
            Nothing saves until you review and apply.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={scan} disabled={scanning} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="enrich-scan-btn">
            {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Sparkles className="h-4 w-4 mr-2"/>}
            {scanning ? "Scanning…" : proposals.length ? "Re-scan" : "Start scan"}
          </Button>
          {proposals.length > 0 && (
            <Button
              onClick={apply}
              disabled={applying || picks.size === 0}
              className="bg-[#4A6B5D] hover:bg-[#3E594D] disabled:opacity-50"
              data-testid="enrich-apply-btn"
            >
              {applying ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <CheckCircle2 className="h-4 w-4 mr-2"/>}
              Apply {picks.size} selected
            </Button>
          )}
        </div>
      </div>

      {scanning && (
        <div className="mt-8 bg-white border border-[#E5E0D8] rounded-md p-10 text-center">
          <Loader2 className="h-8 w-8 mx-auto text-[#4A6B5D] animate-spin"/>
          <p className="text-sm text-[#5C5850] mt-3">
            Looking up addresses…{" "}
            {progress.total > 0 && <span className="font-mono">({progress.done} / {progress.total})</span>}
          </p>
          <p className="text-xs text-[#8A847A] mt-1">Table updates live as batches finish — you can start reviewing.</p>
        </div>
      )}

      {!scanning && proposals.length === 0 && (
        <div className="mt-8 bg-white border border-[#E5E0D8] rounded-md p-10 text-center" data-testid="enrich-empty">
          <MapPin className="h-8 w-8 mx-auto text-[#8A847A] opacity-60"/>
          <p className="text-sm text-[#8A847A] mt-3">Hit "Start scan" to fetch proposals. Nothing writes to your DB until you click Apply.</p>
        </div>
      )}

      {proposals.length > 0 && (
        <>
          <div className="mt-6 grid grid-cols-3 gap-3 max-w-lg">
            <StatTile label="High confidence" value={byConf.high} tone="high" />
            <StatTile label="Medium" value={byConf.medium} tone="medium" />
            <StatTile label="Low / skip" value={byConf.low} tone="low" />
          </div>

          <div className="mt-6 bg-white border border-[#E5E0D8] rounded-md overflow-hidden" data-testid="enrich-table">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F7F5F0]">
                  <th className="w-8 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={picks.size > 0 && picks.size === proposals.length}
                      onChange={(e) => setPicks(e.target.checked ? new Set(proposals.map((p) => p.facility_id)) : new Set())}
                      className="h-4 w-4 accent-[#4A6B5D] cursor-pointer"
                      data-testid="enrich-select-all"
                    />
                  </th>
                  <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Facility</th>
                  <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Proposed address</th>
                  <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {proposals.map((p) => {
                  const cs = CONF_STYLE[p.confidence] || CONF_STYLE.low;
                  const picked = picks.has(p.facility_id);
                  const disabled = !p.address;
                  return (
                    <tr key={p.facility_id} className={`border-t border-[#E5E0D8] ${picked ? "bg-[#F0EBE1]" : ""} ${disabled ? "opacity-50" : ""}`} data-testid={`enrich-row-${p.facility_id}`}>
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={picked}
                          disabled={disabled}
                          onChange={() => toggle(p.facility_id)}
                          className="h-4 w-4 accent-[#4A6B5D] cursor-pointer disabled:cursor-not-allowed"
                          data-testid={`enrich-check-${p.facility_id}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-[#292723]">{p.facility_name}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-[#292723]">
                        {p.address ? (
                          <>
                            <div>{p.address}</div>
                            <div className="text-xs text-[#8A847A]">{[p.city, p.state, p.zip].filter(Boolean).join(", ") || "—"}</div>
                          </>
                        ) : (
                          <span className="italic text-[#8A847A]">AI couldn't identify — skip</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${cs.chip}`}>
                            {p.confidence === "high" ? <CheckCircle2 className="h-3 w-3"/> :
                             p.confidence === "medium" ? <AlertTriangle className="h-3 w-3"/> :
                             <XCircle className="h-3 w-3"/>}
                            {cs.label}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function StatTile({ label, value, tone }) {
  const styles = {
    high:   "bg-[#E9F0EA] border-[#B7CDBB] text-[#3B5C46]",
    medium: "bg-[#FBF3E1] border-[#E1D0AA] text-[#6B4E1E]",
    low:    "bg-[#F7E9E5] border-[#E7C9C1] text-[#8A3E30]",
  }[tone];
  return (
    <div className={`rounded-md p-3 text-center border ${styles}`}>
      <div className="text-[10px] uppercase tracking-[0.1em]">{label}</div>
      <div className="font-mono font-semibold text-xl mt-0.5">{value}</div>
    </div>
  );
}
